import { useCallback } from 'react'

const METADATA_CACHE_KEYS_LIST = 'metadataCacheKeys'
type ValueType = string | object | null | []

export function removeMetadataCacheKeys() {
  const keysJson = sessionStorage.getItem(METADATA_CACHE_KEYS_LIST)
  if (keysJson) {
    try {
      const keys = JSON.parse(keysJson)
      keys.forEach((key: string) => {
        sessionStorage.removeItem(key)
      })
      sessionStorage.removeItem(METADATA_CACHE_KEYS_LIST)
    } catch (error) {
      console.error(`Error parsing metadata cache keys:`, error)
    }
  }
}

/**
 * useMetadataCache is a reusable React hook that enables components to read and write and delete
 * any JavaScript object as key-value pairs. It manages storage transparently, ensuring
 * that all stored metadata is available only when the user's session is active and
 * automatically cleaned up when the session ends.
 *
 * @returns An object with `getMetadata` and `setMetadata` functions.
 */
export function useMetadataCache() {
  const registerKey = (key: string) => {
    try {
      const keysJson = sessionStorage.getItem(METADATA_CACHE_KEYS_LIST)
      const keys = keysJson ? JSON.parse(keysJson) : []
      if (!keys.includes(key)) {
        keys.push(key)
        sessionStorage.setItem(METADATA_CACHE_KEYS_LIST, JSON.stringify(keys))
      }
    } catch (error) {
      console.error(`Error registering metadata key "${key}":`, error)
    }
  }

  /**
   * Retrieves an object from session storage by key.
   *
   * @param key - The key associated with the stored metadata.
   * @returns The parsed object if found, otherwise null.
   */
  const getMetadata = useCallback((key: string): ValueType => {
    try {
      const item = sessionStorage.getItem(key)
      return item ? JSON.parse(item) : null
    } catch (error) {
      console.error(`Error reading metadata for key "${key}":`, error)
      return null
    }
  }, [])

  /**
   * Stores an object in session storage with the specified key.
   *
   * @param key - The key to associate with the stored metadata.
   * @param value - The object to store.
   */
  const setMetadata = useCallback((key: string, value: ValueType): void => {
    try {
      const serializedValue = JSON.stringify(value)
      sessionStorage.setItem(key, serializedValue)

      registerKey(key)
    } catch (error) {
      console.error(`Error setting metadata for key "${key}":`, error)
    }
  }, [])

  /**
   * Deletes the object associated with the given key from the session storage.
   *
   * @param key - The key of the metadata to be deleted.
   */
  const deleteMetadata = useCallback((key: string): void => {
    try {
      sessionStorage.removeItem(key)
      const keysJson = sessionStorage.getItem(METADATA_CACHE_KEYS_LIST)
      if (keysJson) {
        const keys = JSON.parse(keysJson)
        const index = keys.indexOf(key)
        if (index > -1) {
          keys.splice(index, 1)
          sessionStorage.setItem(METADATA_CACHE_KEYS_LIST, JSON.stringify(keys))
        }
      }
    } catch (error) {
      console.error(`Error deleting metadata for key "${key}":`, error)
    }
  }, [])

  return { getMetadata, setMetadata, deleteMetadata }
}
